import { ReactNode, useState } from 'react';
import { api, query } from '../lib/api';
import { useAsync } from '../lib/use-async';
import { ApiModule, AuditEntry } from '../lib/types';
import { formatDateTime, statusTone } from '../lib/format';
import { Badge, Card, Empty, Modal, Notice, PageHead } from '../components/ui';

interface AuditResponse {
  entries: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

interface AuditDetail {
  entry: AuditEntry & { query_string: string | null; response_bytes: number | null; upstream_error: string | null };
  payload: {
    payload: unknown;
    truncated: boolean;
    redacted_fields: string[];
    deleted_at: string | null;
    deleted_by: string | null;
    content_type: string | null;
  } | null;
}

export function Audit() {
  const [filter, setFilter] = useState({
    search: '',
    moduleKey: '',
    decision: '',
    isWrite: '',
    offset: 0,
  });
  const [selected, setSelected] = useState<string | null>(null);

  const modules = useAsync(() => api.get<ApiModule[]>('/api/admin/modules'));
  const result = useAsync(
    () =>
      api.get<AuditResponse>(
        `/api/admin/audit${query({
          search: filter.search || undefined,
          moduleKey: filter.moduleKey || undefined,
          decision: filter.decision || undefined,
          isWrite: filter.isWrite || undefined,
          offset: filter.offset,
          limit: 50,
        })}`,
      ),
    [filter],
  );

  return (
    <>
      <PageHead
        title="Audit log"
        description="Every request with actor, operation and outcome. Write operations additionally carry the complete payload, read operations only metadata."
      />

      {result.error ? <Notice tone="danger">{result.error}</Notice> : null}

      <Card tight>
        <div className="matrix-toolbar">
          <input
            type="search"
            placeholder="Path, person or correlation id"
            value={filter.search}
            onChange={(event) => setFilter({ ...filter, search: event.target.value, offset: 0 })}
          />
          <select
            value={filter.moduleKey}
            onChange={(event) => setFilter({ ...filter, moduleKey: event.target.value, offset: 0 })}
          >
            <option value="">all modules</option>
            {(modules.data ?? []).map((module) => (
              <option key={module.id} value={module.key}>
                {module.key}
              </option>
            ))}
          </select>
          <select
            value={filter.decision}
            onChange={(event) => setFilter({ ...filter, decision: event.target.value, offset: 0 })}
          >
            <option value="">all decisions</option>
            <option value="allowed">allowed</option>
            <option value="denied">denied</option>
          </select>
          <select
            value={filter.isWrite}
            onChange={(event) => setFilter({ ...filter, isWrite: event.target.value, offset: 0 })}
          >
            <option value="">reading and writing</option>
            <option value="true">writing only</option>
            <option value="false">reading only</option>
          </select>
          <span className="muted">{result.data?.total ?? 0} entries</span>
        </div>

        {result.data?.entries.length ? (
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Operation</th>
                <th>Actor</th>
                <th>Application</th>
                <th>Outcome</th>
                <th className="num">Duration</th>
              </tr>
            </thead>
            <tbody>
              {result.data.entries.map((entry) => (
                <tr key={entry.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(entry.id)}>
                  <td className="nowrap">{formatDateTime(entry.ts)}</td>
                  <td>
                    <span className="mono">{entry.operation_key ?? `${entry.http_method} ${entry.path}`}</span>
                    <div className="muted mono tiny">
                      {entry.http_method} {entry.path}
                    </div>
                  </td>
                  <td>
                    {entry.actor_label ?? '—'}
                    <div className="muted tiny">
                      {entry.actor_type}
                    </div>
                  </td>
                  <td>{entry.application_name ?? '—'}</td>
                  <td>
                    {entry.decision === 'denied' ? (
                      <Badge tone="danger">denied</Badge>
                    ) : (
                      <Badge tone={statusTone(entry.status_code)}>{entry.status_code ?? 'open'}</Badge>
                    )}
                    {entry.is_write ? (
                      <Badge tone="warn" >writing</Badge>
                    ) : null}
                  </td>
                  <td className="num">{entry.duration_ms === null ? '—' : `${entry.duration_ms} ms`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No entries for this selection.</Empty>
        )}

        <div className="row end" style={{ padding: 12 }}>
          <button
            className="small"
            disabled={filter.offset === 0}
            onClick={() => setFilter({ ...filter, offset: Math.max(0, filter.offset - 50) })}
          >
            Back
          </button>
          <button
            className="small"
            disabled={(result.data?.entries.length ?? 0) < 50}
            onClick={() => setFilter({ ...filter, offset: filter.offset + 50 })}
          >
            Next
          </button>
        </div>
      </Card>

      {selected ? <DetailDialog id={selected} onClose={() => setSelected(null)} /> : null}
    </>
  );
}

function DetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useAsync(() => api.get<AuditDetail>(`/api/admin/audit/${id}`), [id]);
  const [message, setMessage] = useState<string | null>(null);

  async function deletePayload(): Promise<void> {
    if (!window.confirm('Delete the payload of this entry? The entry itself remains.')) return;
    await api.delete(`/api/admin/audit/${id}/payload`);
    setMessage('Payload deleted. The entry records who did that and when.');
    detail.reload();
  }

  return (
    <Modal title="Audit entry" onClose={onClose} footer={<button onClick={onClose}>Close</button>}>
      {message ? <Notice tone="ok">{message}</Notice> : null}
      {detail.data ? (
        <>
          <table>
            <tbody>
              <Row label="Time" value={formatDateTime(detail.data.entry.ts)} />
              <Row label="Correlation id" value={<span className="mono">{detail.data.entry.correlation_id}</span>} />
              <Row label="Module" value={<span className="mono">{detail.data.entry.module_key}</span>} />
              <Row label="Operation" value={<span className="mono">{detail.data.entry.operation_key ?? '—'}</span>} />
              <Row
                label="Request"
                value={
                  <span className="mono">
                    {detail.data.entry.http_method} {detail.data.entry.path}
                    {detail.data.entry.query_string ?? ''}
                  </span>
                }
              />
              <Row label="Actor" value={`${detail.data.entry.actor_label ?? '—'} (${detail.data.entry.actor_type})`} />
              <Row label="Application" value={detail.data.entry.application_name ?? '—'} />
              <Row label="Credential used" value={detail.data.entry.credential_label ?? '—'} />
              <Row
                label="Impersonated"
                value={detail.data.entry.impersonated_upstream_id ?? <span className="muted">no</span>}
              />
              <Row
                label="Outcome"
                value={
                  detail.data.entry.decision === 'denied'
                    ? `denied: ${detail.data.entry.deny_reason ?? ''}`
                    : `${detail.data.entry.status_code ?? 'open'} in ${detail.data.entry.duration_ms ?? '?'} ms`
                }
              />
              {detail.data.entry.upstream_error ? (
                <Row label="Error of the target API" value={detail.data.entry.upstream_error} />
              ) : null}
              <Row label="Origin" value={detail.data.entry.client_ip ?? '—'} />
            </tbody>
          </table>

          {detail.data.payload ? (
            <div style={{ marginTop: 16 }}>
              <div className="row" style={{ marginBottom: 8 }}>
                <strong>Payload</strong>
                {detail.data.payload.truncated ? <Badge tone="warn">truncated</Badge> : null}
                {detail.data.payload.redacted_fields.length ? (
                  <Badge>masked: {detail.data.payload.redacted_fields.join(', ')}</Badge>
                ) : null}
                {detail.data.payload.deleted_at ? (
                  <Badge tone="danger">
                    deleted on {formatDateTime(detail.data.payload.deleted_at)} by{' '}
                    {detail.data.payload.deleted_by}
                  </Badge>
                ) : (
                  <button className="small danger" style={{ marginLeft: 'auto' }} onClick={() => void deletePayload()}>
                    Delete payload
                  </button>
                )}
              </div>
              {detail.data.payload.deleted_at ? null : (
                <pre className="payload">{JSON.stringify(detail.data.payload.payload, null, 2)}</pre>
              )}
            </div>
          ) : detail.data.entry.is_write ? (
            <Notice tone="warn">There is no payload for this write operation.</Notice>
          ) : (
            <p className="muted">Read operation - only metadata is logged.</p>
          )}
        </>
      ) : (
        <p className="muted">Loading...</p>
      )}
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <tr>
      <td className="muted" style={{ width: 180 }}>
        {label}
      </td>
      <td>{value}</td>
    </tr>
  );
}
